/**
 * PURPOSE:
 * Hook de autenticación pública del visualizador arbitral en HAYLLIS.
 *
 * ROUTE:
 * src/pages/VolleyballViewer/hooks/useVolleyballViewerAuth.js
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  authViewerPublic,
  getViewerPublicStatus,
} from "../services/volleyballViewerPublicService.js";
import {
  clearViewerSession,
  getOrCreateViewerDeviceId,
  readViewerSession,
  saveViewerSession,
} from "../utils/volleyballViewerStorage.js";

function messageFromError(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

export function useVolleyballViewerAuth(matchId) {
  const deviceId = useMemo(() => getOrCreateViewerDeviceId(), []);
  const [status, setStatus] = useState(null);
  const [session, setSession] = useState(() => readViewerSession(matchId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getViewerPublicStatus(matchId);
      setStatus(data);
    } catch (err) {
      setError(
        messageFromError(err, "No se pudo consultar el estado del módulo.")
      );
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const login = useCallback(
    async ({ viewerName, viewerRole, code, remember = false }) => {
      setLoading(true);
      setError(null);
      try {
        const data = await authViewerPublic(matchId, {
          viewerName,
          viewerRole,
          code,
          deviceId,
        });
        const nextSession = {
          matchId: Number(matchId),
          deviceId,
          sessionId: data.sessionId,
          sessionToken: data.sessionToken,
          viewerName: data.viewerName,
          viewerRole: data.viewerRole,
        };
        saveViewerSession(matchId, nextSession, { remember });
        setSession(nextSession);
        await loadStatus();
        return nextSession;
      } catch (err) {
        const message = messageFromError(err, "No se pudo ingresar al módulo.");
        setError(message);
        // Importante: no relanzar el error. El formulario ya muestra el aviso en UI.
        // Relanzarlo puede activar el overlay rojo de React/Vite en desarrollo.
        return null;
      } finally {
        setLoading(false);
      }
    },
    [deviceId, loadStatus, matchId]
  );

  const logout = useCallback(() => {
    clearViewerSession(matchId);
    setSession(null);
  }, [matchId]);

  return {
    status,
    session,
    loading,
    error,
    deviceId,
    loadStatus,
    login,
    logout,
    setError,
  };
}
